import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

import { take } from 'rxjs';

import { noWhitespaceValidator } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { TellentPositionsDataFacade } from '@core/tellent-positions-logic';
import {
  RenamePositionDialogData,
  RenamePositionDialogResult,
} from '@core/tellent-positions-types';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  FormErrorDirective,
  FormFieldComponent,
  InputComponent,
} from '@core/user-interface-legacy';

@Component({
  selector: 'tlc-rename-position-dialog',
  templateUrl: './rename-position-dialog.component.html',
  styleUrls: ['./rename-position-dialog.component.less'],
  imports: [
    DialogBodyComponent,
    TranslatePipe,
    ButtonComponent,
    DialogHeaderComponent,
    InputComponent,
    DialogFooterComponent,
    ReactiveFormsModule,
    ButtonLoadingDirective,
    FormErrorDirective,
    FormFieldComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RenamePositionDialogComponent implements DialogComponentI<
  RenamePositionDialogData,
  RenamePositionDialogResult
> {
  private readonly positionsDataFacade = inject(TellentPositionsDataFacade);
  private readonly dialogRef = inject(DialogRef);
  private readonly i18n = inject(I18nService);

  protected readonly nameFieldErrors = {
    ['already_taken']: this.i18n.translate(
      'tlc.positions.rename_position_dialog.errors.already_taken',
    ),
    ['required']: this.i18n.translate('tlc.positions.rename_position_dialog.errors.required'),
  };

  public nameControl = new FormControl('', [Validators.required, noWhitespaceValidator]);
  public loading = signal<boolean>(false);

  private positionGuid = signal<string>('');

  public setDialogData(data: RenamePositionDialogData, triggerUpdate: boolean): void {
    this.positionGuid.set(data.positionGuid);
    this.nameControl.setValue(data.positionName);
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public save(): void {
    if (this.nameControl.invalid) return;
    const name = this.nameControl.value!;

    this.loading.set(true);

    this.positionsDataFacade
      .renamePosition(this.positionGuid(), name)
      .pipe(take(1))
      .subscribe(result => {
        this.loading.set(false);

        if (result.isSuccess) {
          this.dialogRef.close({ success: true });
        } else {
          const errorKey = result.action.payload.error.error.errors?.name?.[0];
          if (errorKey) {
            this.nameControl.setErrors({ [errorKey]: true });
          }
        }
      });
  }
}
